import { Box, Stack, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import { DateTime } from 'luxon'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import CustomButton from '@/components/inputs/CustomButton'
import RampCurveChart from '@/features/resource/components/rampCurves/RampCurveChart'
import type { RampCurve } from '@/features/resource/types'
import { convertToKilo } from '@/features/resource/utils/convertToKilo'
import { UNIT_SYMBOLS } from '@/utils/powerEnergyTransformations'

export type RampCurveDetailsProps = {
  rampCurve: RampCurve
  onEdit: () => void
}

const RampCurveDetails = ({ rampCurve, onEdit }: RampCurveDetailsProps) => {
  const { t } = useTranslation()
  const [showAllEntries, setShowAllEntries] = useState(false)

  const displayedEntries = showAllEntries ? rampCurve.rampCurveEntries : rampCurve.rampCurveEntries.slice(0, 10)

  const hasMoreEntries = rampCurve.rampCurveEntries.length > 10

  const settingsColumns = useMemo(() => {
    return [
      {
        field: 'name',
        headerName: t('resources.ramp_curves.settings.name'),
        flex: 1,
      },
      {
        field: 'meteringPortId',
        headerName: t('resources.ramp_curves.settings.metering_port_id'),
        flex: 1,
      },
      {
        field: 'verifiedAt',
        headerName: t('resources.ramp_curves.settings.verified_at'),
        flex: 1,
      },
      {
        field: 'direction',
        headerName: t('resources.ramp_curves.settings.direction'),
        flex: 1,
      },
    ]
  }, [t])

  const entriesColumns = useMemo(() => {
    return [
      {
        field: 'entry',
        headerName: t('resources.ramp_curves.entries.entry'),
        flex: 1,
      },
      {
        field: 'duration',
        headerName: t('resources.ramp_curves.entries.duration_label'),
        flex: 1,
      },
      {
        field: 'consumption',
        headerName: t('resources.ramp_curves.entries.consumption_label'),
        flex: 1,
      },
    ]
  }, [t])

  return (
    <Stack alignItems={'flex-start'} direction={'column'} sx={{ gap: 4, paddingX: 7, paddingY: 4 }}>
      {/* Ramp Curve Settings Table */}
      <Table aria-label={t('resources.ramp_curves.details.settings_table')} size="small" sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow>
            {settingsColumns.map((column) => (
              <TableCell key={column.field}>{column.headerName}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>{rampCurve.name}</TableCell>
            <TableCell>{rampCurve.meteringPortId || '-'}</TableCell>
            <TableCell>
              {rampCurve.verifiedAt ? DateTime.fromISO(rampCurve.verifiedAt).toFormat('dd/MM/yyyy, HH:mm') : '-'}
            </TableCell>
            <TableCell>
              {t(
                `resources.ramp_curves.direction.${rampCurve.direction.toLowerCase() as Lowercase<typeof rampCurve.direction>}`,
              )}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      {/* Ramp Curve Entries Table */}
      <Table aria-label={t('resources.ramp_curves.details.entries_table')} size="small" sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow>
            {entriesColumns.map((column) => (
              <TableCell key={column.field}>{column.headerName}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {displayedEntries.map((entry, index) => (
            <TableRow key={entry.id}>
              <TableCell>{index + 1}</TableCell>
              <TableCell>{entry.durationSinceStartMillis} ms</TableCell>
              <TableCell>{`${convertToKilo(entry.consumption)} ${UNIT_SYMBOLS.kilowatts}`}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {hasMoreEntries && (
        <CustomButton color="secondary" size="small" variant="text" onClick={() => setShowAllEntries(!showAllEntries)}>
          {showAllEntries
            ? t('resources.ramp_curves.details.show_less')
            : t('resources.ramp_curves.details.show_full_list', { count: rampCurve.rampCurveEntries.length })}
        </CustomButton>
      )}

      {rampCurve.rampCurveEntries.length > 0 && (
        <Box sx={{ width: '100%' }}>
          <RampCurveChart entries={rampCurve.rampCurveEntries} />
        </Box>
      )}

      <CustomButton variant="text" onClick={onEdit}>
        {t('resources.ramp_curves.edit')}
      </CustomButton>
    </Stack>
  )
}

export default RampCurveDetails
